// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './DeliveryAnalysis.scss';
import { firestore } from '../../context/firebase';
import { useTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';

import { subscribeToDeliveryBales } from '../../shared_components/utils/FirebaseDeliveryUtils';
import LoadingIcon from '../LoadingIcon/LoadingIcon';

// Importing icons
import back_icon from '../../assets/icons/white/back_small.png';
// Section components
import ImageIssuesSection from './ImageIssuesSection';
import BrownPaperContentSection from './BrownPaperContentSection';
import ContaminantsSection from './ContaminantsSection';
import { useAuthStore } from '../../stores/authStore';
import { getAllLabelsetsBelongingToGroup } from '../../shared_components/utils/FirebaseLabelsetsUtils';
import MaterialMismatchSection from './MaterialMismatchSection';

export default function DeliveryAnalysis({
  delivery,
  deliveryId,
  showBale,
  showDeliveryImage,
  hide,
  onShowAiAnalysisChange,
}) {
  const { t } = useTranslation();
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const currentUserUserFlags = useAuthStore((state) => state.currentUser?.info?.user_flags);
  const globalLabelsets = useAuthStore((state) => state.globalLabelsets);
  const [showAiAnalysis, setShowAiAnalysis] = React.useState(false);
  const [importedBalesData, setImportedBalesData] = React.useState({});
  const [analysisSummary, setAnalysisSummary] = React.useState({
    numBalesAnalyzed: null,
    numContaminations: null,
    avgBrownPaperContent: null,
  });

  const unsubscribeImportedBales = React.useRef(null);

  React.useEffect(() => {
    // Subscribe to all imported bales for the delivery
    if (!deliveryId || !currentUserClientId) {
      return;
    }

    if (unsubscribeImportedBales.current) {
      unsubscribeImportedBales.current();
    }
    unsubscribeImportedBales.current = subscribeToDeliveryBales(
      firestore,
      currentUserClientId,
      deliveryId,
      setImportedBalesData,
      true,
    );

    return () => {
      unsubscribeImportedBales.current();
    };
  }, [deliveryId]);

  React.useEffect(() => {
    if (typeof onShowAiAnalysisChange === 'function') {
      onShowAiAnalysisChange(showAiAnalysis);
    }
  }, [showAiAnalysis, onShowAiAnalysisChange]);

  const availableLabelsets = React.useMemo(() => {
    const availableLabelsets = Array.from(Object.keys(delivery?.analytics?.data_per_labelset || {}));

    const materialLabelsetIds = getAllLabelsetsBelongingToGroup(globalLabelsets, 'materials');
    if (materialLabelsetIds.some((labelsetId) => availableLabelsets.includes(labelsetId))) {
      availableLabelsets.push('materials');
    }

    const contaminantsLabelsetIds = getAllLabelsetsBelongingToGroup(globalLabelsets, 'contaminants');
    if (contaminantsLabelsetIds.some((labelsetId) => availableLabelsets.includes(labelsetId))) {
      availableLabelsets.push('contaminants');
    }

    return availableLabelsets;
  }, [delivery, globalLabelsets]);

  const summaryText = React.useMemo(() => {
    if (
      (currentUserUserFlags || [])?.includes('admin') &&
      analysisSummary.numContaminations !== null &&
      analysisSummary.numContaminations > 0
    ) {
      return `${t('contaminants')}: ${analysisSummary.numContaminations || '0'}`;
    } else if (analysisSummary.numBalesAnalyzed !== null) {
      return `${t('bales_analyzed')}: ${analysisSummary.numBalesAnalyzed || '0'}/${Object.keys(delivery?.bales || {}).length}`;
    }
    return null;
  }, [analysisSummary.numContaminations, analysisSummary.numBalesAnalyzed, Object.keys(delivery?.bales || {}).length]);

  return (
    <div className={`delivery--ai-analysis-unified ${isMobileOnly ? 'mobile' : ''} ${hide ? 'hide' : ''}`}>
      {/* AI Analysis Header */}
      <div
        className={`delivery--ai-unified-header ${!showAiAnalysis && isMobileOnly ? 'collapsed' : ''}`}
        onClick={() => setShowAiAnalysis(!showAiAnalysis)}
      >
        <div className='delivery--ai-unified-header-info'>
          <div className='delivery--ai-unified-icon-wrapper'>
            <div className='delivery--ai-unified-badge'>AI</div>
          </div>
          <div className='delivery--ai-unified-header-text'>
            <h3 className='delivery--ai-unified-title'>{t('ai_analysis')}</h3>
            {summaryText && (
              <div className='delivery--ai-unified-summary'>
                <span className='delivery--ai-summary-text warning'>{summaryText}</span>
              </div>
            )}
          </div>
          {delivery?.processing_status === 'running' && <LoadingIcon />}
        </div>
        <div className='delivery--ai-unified-chevron'>
          <img
            src={back_icon}
            alt='Expand'
            className={`delivery--ai-chevron-icon ${showAiAnalysis ? 'expanded' : ''}`}
          />
        </div>
      </div>

      {/* Expanded Content */}

      <div className='delivery--ai-unified-content' style={{ display: showAiAnalysis ? 'block' : 'none' }}>
        <div className='delivery--ai-nested-cards'>
          {/* No AI analysis available for this delivery */}
          {availableLabelsets.length === 0 && (
            <div className='delivery--no-ai-analysis-available'>
              <p>{t('no_ai_analysis_available')}</p>
            </div>
          )}

          {/* Bales Analyzed Card */}
          {availableLabelsets.includes('image_issues') && (
            <ImageIssuesSection
              delivery={delivery}
              deliveryId={deliveryId}
              importedBalesData={importedBalesData}
              showBale={showBale}
              showDeliveryImage={showDeliveryImage}
              setAnalysisSummary={setAnalysisSummary}
            />
          )}

          {/* Material Mismatch Card */}
          {availableLabelsets.includes('materials') && (
            <MaterialMismatchSection delivery={delivery} importedBalesData={importedBalesData} showBale={showBale} />
          )}

          {/* Brown Paper Content Card */}
          {availableLabelsets.includes('brown_paper_content') && (
            <BrownPaperContentSection delivery={delivery} setAnalysisSummary={setAnalysisSummary} />
          )}

          {/* Contaminants Card */}
          {availableLabelsets.includes('contaminants') && (currentUserUserFlags || [])?.includes('admin') && (
            <ContaminantsSection
              delivery={delivery}
              deliveryId={deliveryId}
              importedBalesData={importedBalesData}
              showBale={showBale}
              setAnalysisSummary={setAnalysisSummary}
            />
          )}
        </div>
      </div>
    </div>
  );
}
